import { useEffect } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { ApiClient } from '@schemas'
import { apiClient, baseUrl } from '@/lib/apiClient'
import { reportActorKey, useCurrentUser } from './hooks'

// This transport never inherits staff cookies, actor headers, or sign-in redirects.
export const publicSeriesClient = new ApiClient({
  baseUrl,
  fetchImpl: (input, init) => fetch(input, { ...init, credentials: 'omit', cache: 'no-store' }),
})
export const fresh = {
  staleTime: 0,
  gcTime: 0,
  refetchOnMount: 'always' as const,
  refetchOnWindowFocus: 'always' as const,
  retry: false,
}
export function useSeriesPublication(showId: string) {
  const user = useCurrentUser()
  return useQuery({
    queryKey: ['show-series', 'publication', showId, ...reportActorKey(user.data)],
    queryFn: ({ signal }) => apiClient.getSeriesPublication(showId, signal),
    enabled: Boolean(user.data),
    ...fresh,
  })
}
export function usePublicationMutation(showId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      action:
        | { kind: 'preview' }
        | { kind: 'publish'; token: string; acknowledged: boolean }
        | { kind: 'withdraw'; generation: number }
    ) => {
      if (action.kind === 'preview') return apiClient.previewSeriesPublication(showId)
      if (action.kind === 'publish')
        return apiClient.publishSeriesEdition(showId, action.token, action.acknowledged)
      return apiClient.unpublishSeriesEdition(showId, action.generation)
    },
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['show-series', 'publication', showId] }),
        qc.invalidateQueries({ queryKey: ['public-series'] }),
        qc.invalidateQueries({ queryKey: ['public-page-metadata'] }),
        qc.invalidateQueries({ queryKey: ['public-atlas'] }),
        qc.invalidateQueries({ queryKey: ['public-classes'] }),
      ])
    },
  })
}
function usePublicSeriesTrends(
  slug: string,
  range: { fromYear?: number; toYear?: number },
  enabled: boolean
) {
  return useInfiniteQuery({
    queryKey: ['public-series', slug, 'trends', range],
    queryFn: ({ pageParam, signal }) =>
      publicSeriesClient.getPublicSeriesTrends(
        slug,
        { ...range, limit: 100, ...pageParam },
        signal
      ),
    initialPageParam: undefined as { cursor: string; fromYear: number; toYear: number } | undefined,
    // Later pages keep the first page's years, even if a newer edition is published meanwhile.
    getNextPageParam: page =>
      page.nextCursor
        ? { cursor: page.nextCursor, fromYear: page.fromYear, toYear: page.toYear }
        : undefined,
    enabled,
    ...fresh,
  })
}
/**
 * The whole published report for a range of years. The class catalog arrives in pages and
 * each page's classes are read as one report slice, so a figure on the page covers every
 * published class however many there are. `report` is defined once every slice has loaded.
 */
export function usePublicSeriesAlmanac(
  slug: string,
  range: { fromYear?: number; toYear?: number },
  enabled: boolean
) {
  const catalog = usePublicSeriesTrends(slug, range, enabled)
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = catalog
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage])
  const pages = catalog.data?.pages ?? []
  const years = pages[0] && { fromYear: pages[0].fromYear, toYear: pages[0].toYear }
  const slices = useQueries({
    queries: pages.map(page => {
      const selection = { ...years!, trendKeys: page.trends.map(trend => trend.trendKey) }
      return {
        queryKey: ['public-series', slug, 'report', selection],
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          publicSeriesClient.getPublicSeriesReport(slug, selection, signal),
        enabled: enabled && !isError,
        ...fresh,
      }
    }),
  })
  const loaded = slices.flatMap(slice => (slice.data ? [slice.data] : []))
  const complete = pages.length > 0 && !hasNextPage && loaded.length === pages.length
  // Every catalog page and slice names the set of publications it was read from. If an edition
  // is published or withdrawn between two reads they differ, and everything is read again
  // rather than joined into a report no set of publications ever held.
  const consistent = new Set([...pages, ...loaded].map(response => response.revision)).size <= 1
  const idle = !catalog.isFetching && slices.every(slice => !slice.isFetching)
  const mixed = complete && !consistent && idle
  const qc = useQueryClient()
  useEffect(() => {
    if (mixed) void qc.invalidateQueries({ queryKey: ['public-series', slug] })
  }, [mixed, qc, slug])
  return {
    years,
    report:
      complete && consistent
        ? { ...loaded[0], trends: loaded.flatMap(slice => slice.trends) }
        : undefined,
    error: catalog.error ?? slices.find(slice => slice.error)?.error ?? null,
    refetch: () => {
      void catalog.refetch()
      for (const slice of slices) void slice.refetch()
    },
  }
}
/** Editions with released show results. Optional to the page: without it there are no links. */
export function usePublicSeriesResults(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: ['public-series', slug, 'results'],
    queryFn: ({ signal }) => publicSeriesClient.getPublicSeriesResults(slug, signal),
    enabled,
    ...fresh,
  })
}
